import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, euro } from "@/lib/api";
import { Check, ShieldCheck, Download, Lock, ArrowLeft, Cpu, FileArchive } from "lucide-react";

export default function ProductDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    api.get(`/products/${id}`).then((r) => setP(r.data)).catch(() => setErr(true));
  }, [id]);

  if (err) return <div className="max-w-3xl mx-auto px-4 py-24 text-center text-slate-400" data-testid="product-not-found">Product not found. <Link to="/products" className="text-cyan-400">Back to catalog</Link></div>;
  if (!p) return <div className="max-w-3xl mx-auto px-4 py-24 text-center text-slate-400">Loading…</div>;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-16">
      <Link to="/products" className="inline-flex items-center gap-1.5 text-slate-400 hover:text-cyan-400 text-sm mb-8">
        <ArrowLeft className="w-4 h-4" /> Back to catalog
      </Link>
      <div className="grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2">
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-mono bg-blue-500/10 text-blue-400 border border-blue-500/30">{p.category}</span>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-white mt-4 leading-tight" data-testid="product-title">{p.title}</h1>
          <p className="text-lg text-slate-300 mt-4 leading-relaxed">{p.tagline}</p>

          <div className="tactical-card p-6 mt-8">
            <h3 className="font-display font-semibold text-white flex items-center gap-2"><Cpu className="w-5 h-5 text-cyan-400" /> Overview</h3>
            <p className="text-slate-400 mt-3 leading-relaxed">{p.description || p.tagline}</p>
          </div>

          {p.specs?.length > 0 && (
            <div className="tactical-card p-6 mt-6">
              <h3 className="font-display font-semibold text-white mb-4">Key specifications</h3>
              <div className="grid sm:grid-cols-2 gap-3">
                {p.specs.map((s) => (
                  <div key={s} className="flex items-center gap-2 text-sm text-slate-300">
                    <Check className="w-4 h-4 text-emerald-400 shrink-0" /> {s}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="tactical-card p-6 mt-6">
            <h3 className="font-display font-semibold text-white flex items-center gap-2"><Download className="w-5 h-5 text-cyan-400" /> Delivery</h3>
            <p className="text-slate-400 mt-3 leading-relaxed">
              Delivered as a secure private ZIP package. After confirmed payment you receive an automatic secure
              download link and an email receipt. No account required.
            </p>
          </div>
        </div>

        {/* PURCHASE PANEL */}
        <div>
          <div className="tactical-card p-6 sticky top-24">
            <span className="block text-[10px] font-mono uppercase tracking-widest text-slate-500">Perpetual Licence</span>
            <div className="font-display text-4xl font-extrabold text-white mt-1" data-testid="product-price">{euro(p.price)}</div>
            <p className="text-xs text-slate-500 mt-1">{p.license || "Enterprise Licence"} · one-time payment</p>

            <button onClick={() => nav(`/checkout/${p.id}`)}
              className="w-full mt-6 inline-flex items-center justify-center gap-2 bg-[#2563EB] hover:bg-[#1D4ED8] btn-glow text-white font-semibold px-6 py-3.5 rounded-full"
              data-testid={`buy-now-${p.id}`}>
              <ShieldCheck className="w-5 h-5" /> Buy Now
            </button>

            <div className="mt-5 space-y-2.5 text-sm text-slate-400">
              <div className="flex items-center gap-2"><Lock className="w-4 h-4 text-cyan-400" /> Secure hosted payment</div>
              <div className="flex items-center gap-2"><Download className="w-4 h-4 text-cyan-400" /> Instant secure download</div>
              <div className="flex items-center gap-2"><FileArchive className="w-4 h-4 text-cyan-400" /> Private ZIP package</div>
              <div className="flex items-center gap-2"><Check className="w-4 h-4 text-cyan-400" /> No account needed</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
